import * as React from "react";

import { hasCustomFields } from "../../src/activities";

import DocSection from "./Section";
import GenButton from "../Gen/Button";
import GenSwitch, {switchModes} from "../Gen/Switch";

import DocQuestionReportButton from "./QuestionReportButton";

import {
  deepFindNode,
} from "../../src/doc_helpers";

import {
  applyState,
} from "../../src/state";

type DocActivityProps = ActivityNode & EveryNodeArgs & {
  id: number;
  due_at?: string;
  is_graded: boolean
  name: string;
  placement_inside: boolean;
  custom_entries: Record<string, string>;
};

export default function DocActivity(props: DocActivityProps) {
  const {
    id,
    show_after,
    show_until,
    due_at,
    content,
    custom_entries,

    activityUserState,

    inactive,
    inactiveBoolean,

    currentDocument,
    currentDocumentSetDocumentRelation,
    currentRoleDefinition,
    currentVersion,
    currentUser,
    displayState,
    editState,

    isGated,

    activityActions,
  } = props;

  if (!("lastSyncedAt" in currentDocument)) { return; }

  const activityUserStateLongId = currentDocumentSetDocumentRelation ? `${id}/${currentDocumentSetDocumentRelation.document_set_id}/${currentDocument.id}` : `${id}/${currentDocument.id}`;

  const showAfter = typeof show_after === "string" ? new Date(show_after) : null;
  const showUntil = typeof show_until === "string" ? new Date(show_until) : null;
  const dueAt = typeof due_at === "string" ? new Date(due_at) : null;

  const currentUserForActivity = {
    ...currentUser,
    addState: (state: StrippedState) => currentUser.activityUserStatesByActivityId[id]._actions.addState(state),
    addStateTargetType: "activity" as const,
    addStateTargetId: id,
    logEvent: () => {},
  };

  const hasStarted = !showAfter || showAfter.getTime() < new Date().getTime();

  if (!activityUserState || !("lastSyncedAt" in activityUserState)) { return null; } // TODO REPLACE WITH LOADING

  const hasBeenSubmitted = activityUserState.state?.submit_button?.inactive?.value === 2;
  const submittedAt = hasBeenSubmitted ? activityUserState.state?.submit_button?.inactive?.updatedAt : undefined;

  let hideBadge: React.ReactElement;
  if (currentRoleDefinition.isLearner || isGated || !currentDocumentSetDocumentRelation) {
    let badgeText: string;
    if (!hasStarted) {
      return null;
    } if (submittedAt) {
      const formattedSubmittedAt = (new Date(submittedAt)).toLocaleDateString("en-US");

      badgeText = `Submitted: ${formattedSubmittedAt}`;

      if (activityUserState.reviewed_at) {
        badgeText += " (Reviewed)";
      }
    } else {
      badgeText = dueAt ? `Due: ${dueAt.toLocaleDateString("en-US")}` : "Due";
    }

    hideBadge = <div className="doc-due-date-badge due-at">{badgeText}</div>;
  } else {
    const switchOff = showUntil && showUntil.getTime() === (new Date(0)).getTime();

    hideBadge = (
      <GenSwitch
        value={switchOff ? switchModes.OFF : switchModes.ON}
        onClick={(newSwitchMode) => {
          switch (newSwitchMode) {
            case switchModes.ON:
              if (currentDocumentSetDocumentRelation) {
                if ("document_activity_amendment" in props && props.document_activity_amendment) {
                  props.document_activity_amendment._actions.setEditFields({show_until: null});
                  props.document_activity_amendment._actions.update();
                } else if("document_set_document_activity" in props) {
                  props.document_set_document_activity._actions.setEditFields({show_until: null});
                  props.document_set_document_activity._actions.update();
                } else {
                  if (activityActions) {
                    fetch(`/document_set_document_relations/${currentDocumentSetDocumentRelation.id}/document_set_document_activities`, {
                      method: "POST",
                      body: JSON.stringify({
                        activity_id: id,
                      }),
                      headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                      },
                    })
                      .then((response) => {
                        if (!response.ok) { throw new Error(`${response.status} response`); }

                        return response.json();
                      })
                      .then(() => {
                        activityActions.currentDocumentSetDocumentActivitiesActions.reload();
                      })
                      .catch(() => {});
                  }
                }
              } else if ("document_activity" in props) {
                props.document_activity._actions.setEditFields({show_until: null});
                props.document_activity._actions.update();
              } else {
                if (activityActions) {
                    fetch(`/documents/${currentDocument.id}/document_activities`, {
                      method: "POST",
                      body: JSON.stringify({
                        activity_id: id,
                      }),
                      headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                      },
                    })
                      .then((response) => {
                        if (!response.ok) { throw new Error(`${response.status} response`); }

                        return response.json();
                      })
                      .then(() => {
                        activityActions.currentDocumentActivitiesActions.reload();
                      })
                      .catch(() => {});
                }
              }

              break;
            case switchModes.OFF:
              if (currentDocumentSetDocumentRelation) {
                if ("document_activity_amendment" in props && props.document_activity_amendment) {
                  props.document_activity_amendment._actions.setEditFields({show_until: (new Date(0)).toString()});
                  props.document_activity_amendment._actions.update();
                } else if ("document_activity" in props) {
                  if (activityActions) {
                    fetch(`/document_set_document_relations/${currentDocumentSetDocumentRelation.id}/document_activity_amendments`, {
                      method: "POST",
                      body: JSON.stringify({
                        document_activity_id: props.document_activity.id,
                        show_until: (new Date(0)).toString(),
                      }),
                      headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                      },
                    })
                      .then((response) => {
                        if (!response.ok) { throw new Error(`${response.status} response`); }

                        return response.json();
                      })
                      .then(() => {
                        activityActions.currentDocumentActivityAmendmentsActions.reload();
                      })
                      .catch(() => {});
                  }
                } else if("document_set_document_activity" in props) {
                  props.document_set_document_activity._actions.setEditFields({show_until: (new Date(0)).toString()});
                  props.document_set_document_activity._actions.update();
                }
              } else if ("document_activity" in props) {
                props.document_activity._actions.setEditFields({show_until: (new Date(0)).toString()});
                props.document_activity._actions.update();
              }

              break;
          }
        }}
      />
    );
  }

  const activityContent = content;
  const hasTextInputs = deepFindNode(activityContent, (node) => node.type === "textInput");
  const hasOpenAiChats = deepFindNode(activityContent, (node) => node.type === "openAiChat");

  const activitySections = applyState(activityContent, activityUserState.state || {});

  // TODO: PULL UP
  if (hasBeenSubmitted) {
    activitySections.forEach((activitySection) => {
      activitySection.content
        .filter((node) => node.type === "textInput")
        .forEach((node) => {
          node.inactive = 2;
        });
    });
  }

  let activityReview: React.ReactNode;
  if (activityUserState.reviewed_at) {
    activityReview = (
      <div className="activity-review">
        <h4>Review:</h4>
        <div className="activity-review-inside">
          <p>{activityUserState.review_comment}</p>
        </div>
      </div>
    );
  }

  let questionReportButton: React.ReactNode;
  if (!hasTextInputs) {
    questionReportButton = <DocQuestionReportButton/>;
  }

  let exportPdfButton: React.ReactNode;
  if (hasTextInputs || hasOpenAiChats) {
    exportPdfButton = (
      <GenButton
        name="Export PDF"
        href={`/activity_user_states/${activityUserStateLongId}.pdf`}
        target="_blank"
        highlighted
      />
    );
  }

  let editActivityButton: React.ReactNode;
  if (currentUser.is_admin || currentRoleDefinition?.name === "ReelDx_Faculty") {
    editActivityButton = (
      <>
        <GenButton
          name="Edit Activity"
          href={`/activities/${id}/edit`}
          target="_blank"
          highlighted
        />
        {hasCustomFields(content) && (
          <GenButton
            name="Customize Activity"
            target="_blank"
            highlighted
            onClick={() => displayState.setOpenMenu({name: "CUSTOMIZE_ACTIVITIES_MODAL", activityId: id})}
          />
        )}
      </>
    );
  }

  return (
    activitySections.filter((section) => !section.invisible).map((activitySection, activitySectionIndex) => (
      <DocSection
        key={activitySection.uid}
        {...activitySection}
        headerColor="#F1F3F3"
        titleBadge={<div className="skill-badge bg-sky-600">Activity</div>}
        rightContent={(
          <>
            {editActivityButton}
            {exportPdfButton}
            {hideBadge}
          </>
        )}
        aboveContent={activitySectionIndex === 0 && activityReview}
        currentDocument={currentDocument}
        currentVersion={currentVersion}
        editState={editState}
        isGated={isGated}
        currentUser={currentUserForActivity}
        currentRoleDefinition={currentRoleDefinition}
        currentDocumentSetDocumentRelation={currentDocumentSetDocumentRelation}
        currentActivityUserState={activityUserState}
        currentActivityCustomEntries={custom_entries}
        reportButton={questionReportButton}
        displayState={displayState}
        inactive={inactive}
        inactiveBoolean={inactiveBoolean}
      />
    ))
  );
}
